import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import "./ShopifyDevelopment.css";

const CONTACT = "/contact?service=Shopify%20Store%20Development";

const builds = [
  ["01", "bag", "Custom Shopify Stores", "A storefront planned around the catalog, not a theme installed and left as-is."],
  ["02", "theme", "Custom Theme Development", "A theme built for the brand when settings on an existing theme are not enough."],
  ["03", "tune", "Theme Customization", "Adjustments to layout, type, and product presentation inside a theme that already fits."],
  ["04", "product", "Product Pages", "The details, variants, and actions a shopper needs on a single product."],
  ["05", "grid", "Collection Pages", "Groups of products arranged so people can browse a category."],
  ["06", "nav", "Navigation", "Menus that follow how customers look for what you sell."],
  ["07", "search", "Search & Filtering", "Search and filters matched to the catalog, so products are easier to narrow down."],
  ["08", "cart", "Cart Experience", "A cart that shows what is being purchased before checkout."],
  ["09", "account", "Customer Accounts", "Account access for orders and returning customers."],
  ["10", "screens", "Responsive Storefronts", "The same store, laid out for phones, tablets, and desktops."]
];

const capabilities = [
  ["card", "Payments", "Checkout payments through the options Shopify supports for the store."],
  ["truck", "Shipping", "Delivery choices shown where the shopper decides how the order arrives."],
  ["box", "Inventory", "Product availability kept in step with how the catalog is managed."],
  ["user", "Customer Accounts", "A place for returning customers to see orders and saved details."],
  ["search", "Search", "A way to find products by name and by the filters the catalog needs."],
  ["tag", "Product Management", "Products, variants, and collections organized so the storefront stays accurate."],
  ["megaphone", "Marketing Tools", "Connections for campaigns and on-site messages the business chooses to use."],
  ["chart", "Analytics", "A way to see how shoppers move through the store."],
  ["puzzle", "Third-Party Apps", "Apps added when Shopify's built-in tools are not enough for the workflow."],
  ["link", "Business Integrations", "Connections to other systems the business already runs, when those systems allow it."]
];

const journey = [
  ["Discover", "Browse categories and find a product."],
  ["Product", "Read the details, choose a variant, and decide."],
  ["Cart", "Review what is being purchased."],
  ["Checkout", "Move into Shopify's supported checkout."],
  ["Customer", "Land as a buyer, with an order to return to."]
];

const process = [
  ["Strategy", "Understand the business, products, customers, goals, and requirements."],
  ["Store Structure", "Define navigation, collections, product organization, and content structure."],
  ["Experience", "Design the storefront experience and customer journey."],
  ["Development", "Build and customize the Shopify storefront and required functionality."],
  ["Integrations", "Connect relevant business, payment, shipping, marketing, and analytics systems."],
  ["Testing", "Check products, navigation, cart, checkout flow, responsiveness, and integrations."],
  ["Launch", "Deploy the store and verify the production experience."],
  ["Support", "Continue improving and maintaining the store."]
];

const hubNodes = [
  ["pay", "Payments", "18%", "18%"],
  ["ship", "Shipping", "82%", "20%"],
  ["stock", "Inventory", "88%", "52%"],
  ["crm", "CRM", "72%", "82%"],
  ["mail", "Email", "42%", "88%"],
  ["stats", "Analytics", "14%", "74%"],
  ["ads", "Marketing", "12%", "42%"],
  ["ops", "Business systems", "50%", "10%"]
];

const hubMobile = [
  ["pay", 22, 26],
  ["ship", 78, 26],
  ["stock", 20, 66],
  ["crm", 80, 66],
  ["mail", 22, 84],
  ["stats", 78, 84],
  ["ads", 50, 93],
  ["ops", 50, 9]
];

const principles = [
  ["Business First", "Understand the business before deciding how the store should work."],
  ["Custom Where It Matters", "Use Shopify's platform while customizing the experience where the business requires it."],
  ["Customer Focused", "Make products easier to discover, understand, and purchase."],
  ["Ready to Grow", "Build a foundation that can evolve as the catalog and business change."]
];

const faqs = [
  ["Can you customize an existing Shopify store?", "Yes, when the current theme and apps can support the change. We review what should stay, what should be adjusted, and what is safer to replace."],
  ["Can you build a Shopify store from scratch?", "Yes. A new store starts from the products, the customers, and how orders should move, then goes through structure, storefront design, and build."],
  ["Can you customize Shopify themes?", "Yes. That can mean adjusting an existing theme or building a custom theme when the catalog and brand need more than theme settings allow."],
  ["Can you help organize products and collections?", "Yes. Navigation, collections, and product organization follow how customers look for what you sell."],
  ["Can you integrate third-party tools?", "Yes, where Shopify or the other system offers an app, API, or supported connection. Typical cases are payments, shipping, email, analytics, and marketing tools."],
  ["Can you improve the mobile shopping experience?", "Yes. Product pages, navigation, and the cart are designed for phones as well as larger screens. Checkout itself stays within Shopify's supported flow."],
  ["Can you help after the store launches?", "Yes. Support can cover fixes, updates, and later changes. That scope is agreed separately from the initial build."]
];

const products = [
  ["Linen Overshirt", "$128"],
  ["Studio Tote", "$64"],
  ["Ceramic Cup", "$36"]
];

function useInView(threshold = 0.22) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setShown(true);
      return undefined;
    }
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setShown(true);
        observer.disconnect();
      }
    }, { threshold, rootMargin: "0px 0px -8% 0px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, shown];
}

function Reveal({ children, className = "", delay = 0 }) {
  const [ref, shown] = useInView();
  return (
    <div ref={ref} className={`sh-reveal ${shown ? "is-in" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function Kicker({ index, label }) {
  return (
    <p className="sh-kicker">
      <span>{index}</span>
      <span>{label}</span>
    </p>
  );
}

function ShopGlyph({ type }) {
  const common = { width: 15, height: 15, viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: 1.4, "aria-hidden": true };
  if (type === "bag") {
    return <svg {...common}><path d="M4 5h8l-.6 8H4.6L4 5z" /><path d="M6 5V4a2 2 0 0 1 4 0v1" /></svg>;
  }
  if (type === "theme") {
    return <svg {...common}><rect x="2" y="2.5" width="12" height="11" rx="1" /><path d="M2 5.5h12M6 5.5V13.5" /></svg>;
  }
  if (type === "tune") {
    return <svg {...common}><path d="M3 4h10M3 8h10M3 12h10" /><circle cx="6" cy="4" r="1.2" fill="currentColor" /><circle cx="10" cy="8" r="1.2" fill="currentColor" /><circle cx="7" cy="12" r="1.2" fill="currentColor" /></svg>;
  }
  if (type === "product") {
    return <svg {...common}><rect x="3" y="3" width="10" height="10" rx="1" /><path d="M3 10l3-3 2 2 2-2 3 3" /></svg>;
  }
  if (type === "grid") {
    return <svg {...common}><rect x="2.5" y="2.5" width="4.5" height="4.5" /><rect x="9" y="2.5" width="4.5" height="4.5" /><rect x="2.5" y="9" width="4.5" height="4.5" /><rect x="9" y="9" width="4.5" height="4.5" /></svg>;
  }
  if (type === "nav") {
    return <svg {...common}><path d="M2.5 4h11M2.5 8h7M2.5 12h9" /></svg>;
  }
  if (type === "search") {
    return <svg {...common}><circle cx="7" cy="7" r="3.2" /><path d="M9.4 9.4L13 13" /></svg>;
  }
  if (type === "cart") {
    return <svg {...common}><path d="M2 3h2l1.2 7h7.2l1.2-4.5H5" /><circle cx="7" cy="13" r="1" fill="currentColor" /><circle cx="12" cy="13" r="1" fill="currentColor" /></svg>;
  }
  if (type === "account" || type === "user") {
    return <svg {...common}><circle cx="8" cy="6" r="2.2" /><path d="M3.5 13.2c.8-2 2.4-3 4.5-3s3.7 1 4.5 3" /></svg>;
  }
  if (type === "screens") {
    return <svg {...common}><rect x="2" y="3" width="9" height="7" rx="1" /><path d="M4 12.5h5M12 6.5h2v5h-2" /></svg>;
  }
  if (type === "card") {
    return <svg {...common}><rect x="2" y="4" width="12" height="8" rx="1" /><path d="M2 7h12" /></svg>;
  }
  if (type === "truck") {
    return <svg {...common}><path d="M2 5h7v6H2zM9 7h3l2 2v2h-5" /><circle cx="4.5" cy="12" r="1" fill="currentColor" /><circle cx="11.5" cy="12" r="1" fill="currentColor" /></svg>;
  }
  if (type === "box") {
    return <svg {...common}><path d="M2.5 5.5L8 3l5.5 2.5L8 8 2.5 5.5z" /><path d="M2.5 5.5V11L8 13.5 13.5 11V5.5M8 8v5.5" /></svg>;
  }
  if (type === "tag") {
    return <svg {...common}><path d="M3 8.5V3h5.5L13 7.5 7.5 13 3 8.5z" /><circle cx="6" cy="6" r="0.8" fill="currentColor" /></svg>;
  }
  if (type === "megaphone") {
    return <svg {...common}><path d="M3 7v2l8 3V4L3 7zM11 6.5c1 .6 1.6 1.2 1.6 1.5S12 9 11 9.5" /></svg>;
  }
  if (type === "chart") {
    return <svg {...common}><path d="M3 13V8M7 13V5M11 13V3" /></svg>;
  }
  if (type === "puzzle") {
    return <svg {...common}><path d="M6 3h4v2a1.2 1.2 0 0 0 2 1V9h-2a1.2 1.2 0 0 0-1 2H6V9a1.2 1.2 0 0 1-2-1V6h2V3z" /></svg>;
  }
  return <svg {...common}><path d="M6 8H2M10 4l4 4-4 4M10 8h4" /></svg>;
}

function Storefront() {
  return (
    <div className="sh-store">
      <div className="sh-store-chrome">
        <i /><i /><i />
        <span>yourstore.com</span>
      </div>
      <div className="sh-store-body">
        <div className="sh-store-nav sh-store-bit" style={{ animationDelay: "760ms" }}>
          <strong>Atelier</strong>
          <nav>
            <span>Shop</span>
            <span>Collections</span>
            <span>About</span>
          </nav>
          <div className="sh-store-tools">
            <em>Search</em>
            <b>2</b>
          </div>
        </div>
        <div className="sh-store-cats sh-store-bit" style={{ animationDelay: "880ms" }}>
          {["New", "Apparel", "Home", "Objects"].map((item) => <span key={item}>{item}</span>)}
        </div>
        <div className="sh-store-grid">
          {products.map(([name, price], index) => (
            <article key={name} className="sh-store-card sh-store-bit" style={{ animationDelay: `${980 + index * 90}ms` }}>
              <div className={`sh-swatch is-${index}`} />
              <h3>{name}</h3>
              <div>
                <span>{price}</span>
                <button type="button">Add</button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}

function JourneyPreview({ index }) {
  if (index === 1) {
    return (
      <div className="sh-mini sh-mini-product">
        <div className="sh-swatch is-0" />
        <div>
          <strong>Linen Overshirt</strong>
          <span>$128</span>
          <em>Sand · Olive</em>
        </div>
      </div>
    );
  }
  if (index === 2) {
    return (
      <div className="sh-mini sh-mini-cart">
        <div><b>Linen Overshirt</b><span>$128</span></div>
        <div><b>Studio Tote</b><span>$64</span></div>
        <p><span>Subtotal</span><strong>$192</strong></p>
      </div>
    );
  }
  if (index === 3) {
    return (
      <div className="sh-mini sh-mini-check">
        <span>Contact</span>
        <span>Delivery</span>
        <span className="is-pay">Payment</span>
        <small>Continues in Shopify checkout</small>
      </div>
    );
  }
  if (index === 4) {
    return (
      <div className="sh-mini sh-mini-account">
        <strong>Order confirmed</strong>
        <span>Linen Overshirt · Studio Tote</span>
        <em>Ready in the customer account</em>
      </div>
    );
  }
  return (
    <div className="sh-mini sh-mini-discover">
      {["New", "Apparel", "Home"].map((item) => <span key={item}>{item}</span>)}
    </div>
  );
}

function StoreJourney() {
  const [active, setActive] = useState(0);
  const [ref, shown] = useInView(0.35);
  const refs = useRef([]);

  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    let observer;

    const bind = () => {
      observer?.disconnect();
      if (desktop.matches || reduced.matches) return;
      const nodes = refs.current.filter(Boolean);
      observer = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(Number(visible.target.dataset.index));
      }, { threshold: [0.6], rootMargin: "-30% 0px -40% 0px" });
      nodes.forEach((node) => observer.observe(node));
    };

    bind();
    desktop.addEventListener("change", bind);
    return () => {
      desktop.removeEventListener("change", bind);
      observer?.disconnect();
    };
  }, []);

  return (
    <div ref={ref} className={`sh-journey ${shown ? "is-live" : ""}`}>
      <div className="sh-journey-track" style={{ "--sh-fill": `${(active / (journey.length - 1)) * 100}%` }}>
        <div className="sh-journey-rail" />
        <div className="sh-journey-fill" />
        {journey.map(([title, text], index) => (
          <button
            key={title}
            type="button"
            data-index={index}
            ref={(node) => { refs.current[index] = node; }}
            className={`sh-journey-step ${active === index ? "is-active" : ""}`}
            aria-pressed={active === index}
            onMouseEnter={() => setActive(index)}
            onFocus={() => setActive(index)}
            onClick={() => setActive(index)}
          >
            <span className="sh-journey-mark">{String(index + 1).padStart(2, "0")}</span>
            <span>
              <strong>{title}</strong>
              <em>{text}</em>
            </span>
          </button>
        ))}
      </div>
      <div className="sh-journey-preview" key={active}>
        <p>{String(active + 1).padStart(2, "0")}</p>
        <h3>{journey[active][0]}</h3>
        <JourneyPreview index={active} />
      </div>
    </div>
  );
}

function ProductStage() {
  const [ref, shown] = useInView(0.3);
  return (
    <div ref={ref} className={`sh-product ${shown ? "is-live" : ""}`}>
      <div className="sh-product-shot">
        <div className="sh-swatch is-1" />
      </div>
      <div className="sh-product-info">
        <p>Apparel</p>
        <h3>Linen Overshirt</h3>
        <strong>$128</strong>
        <div className="sh-variants">
          <span className="is-on">Sand</span>
          <span>Olive</span>
          <span>Ink</span>
        </div>
        <div className="sh-qty">
          <span>Qty 1</span>
          <button type="button">Add to cart</button>
        </div>
        <ul>
          <li>Fabric</li>
          <li>Fit</li>
          <li>Care</li>
        </ul>
      </div>
    </div>
  );
}

function CommerceHub() {
  const [ref, shown] = useInView(0.35);
  const [active, setActive] = useState(null);
  return (
    <div ref={ref} className={`sh-hub ${shown ? "is-live" : ""} ${active ? "is-hot" : ""}`}>
      <div className="sh-hub-stage">
        <svg className="sh-hub-lines" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {hubNodes.map(([id, , x, y], index) => (
            <line
              key={id}
              x1="50"
              y1="50"
              x2={parseFloat(x)}
              y2={parseFloat(y)}
              className={active === id ? "is-hot" : ""}
              style={{ "--d": `${180 + index * 70}ms` }}
            />
          ))}
        </svg>
        <svg className="sh-hub-lines sh-hub-lines-mobile" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {hubMobile.map(([id, x, y], index) => (
            <line
              key={id}
              x1="50"
              y1="46"
              x2={x}
              y2={y}
              className={active === id ? "is-hot" : ""}
              style={{ "--d": `${180 + index * 70}ms` }}
            />
          ))}
        </svg>
        <div className="sh-hub-core">
          <ShopGlyph type="bag" />
          <strong>Shopify Store</strong>
          <em>Storefront</em>
        </div>
        {hubNodes.map(([id, label, x, y], index) => (
          <button
            key={id}
            type="button"
            className={`sh-hub-node ${active === id ? "is-hot" : ""}`}
            data-id={id}
            style={{ "--x": x, "--y": y, "--n": `${420 + index * 60}ms` }}
            onMouseEnter={() => setActive(id)}
            onMouseLeave={() => setActive(null)}
            onFocus={() => setActive(id)}
            onBlur={() => setActive(null)}
          >
            {label}
          </button>
        ))}
      </div>
      <p className="sh-hub-caption">
        {active
          ? `${hubNodes.find(([id]) => id === active)[1]} connected when that system allows it.`
          : "The store at the center. Business systems connected around it."}
      </p>
    </div>
  );
}

function DevicePair() {
  const [ref, shown] = useInView(0.3);
  return (
    <div ref={ref} className={`sh-devices ${shown ? "is-live" : ""}`}>
      <div className="sh-desktop">
        <div className="sh-store-chrome"><i /><i /><i /><span>yourstore.com</span></div>
        <div className="sh-desktop-row">
          <div className="sh-swatch is-0" />
          <div>
            <strong>Linen Overshirt</strong>
            <span>$128</span>
            <em>Sand</em>
          </div>
          <div className="sh-swatch is-2" />
          <div>
            <strong>Ceramic Cup</strong>
            <span>$36</span>
            <em>Stone</em>
          </div>
        </div>
      </div>
      <div className="sh-link" aria-hidden="true" />
      <div className="sh-phone">
        <div className="sh-phone-chrome"><i /><i /><i /><span>yourstore.com</span></div>
        <div className="sh-phone-bar" />
        <div className="sh-swatch is-0" />
        <strong>Linen Overshirt</strong>
        <span>$128</span>
        <em>Sand</em>
        <button type="button">Add to cart</button>
      </div>
    </div>
  );
}

function ProcessViz({ index }) {
  if (index === 0) {
    return (
      <div className="sh-viz sh-viz-net">
        {["Goals", "Customers", "Products"].map((label) => <span key={label}>{label}</span>)}
      </div>
    );
  }
  if (index === 1) {
    return (
      <div className="sh-viz sh-viz-flow">
        {["Collections", "Products", "Navigation"].map((label, step) => (
          <React.Fragment key={label}>
            {step > 0 ? <i aria-hidden="true" /> : null}
            <span>{label}</span>
          </React.Fragment>
        ))}
      </div>
    );
  }
  if (index === 2) {
    return (
      <ul className="sh-viz sh-viz-layers">
        {["Storefront", "Product page", "Cart"].map((label, step) => (
          <li key={label}><em>{String(step + 1).padStart(2, "0")}</em><span>{label}</span></li>
        ))}
      </ul>
    );
  }
  if (index === 3) {
    return (
      <ul className="sh-viz sh-viz-layers">
        {["Theme", "Components", "Custom functionality"].map((label, step) => (
          <li key={label}><em>{String(step + 1).padStart(2, "0")}</em><span>{label}</span><b /></li>
        ))}
      </ul>
    );
  }
  if (index === 4) {
    return (
      <div className="sh-viz sh-viz-net">
        {["Payments", "Shipping", "Marketing", "Analytics"].map((label) => <span key={label}>{label}</span>)}
      </div>
    );
  }
  if (index === 5) {
    return (
      <ul className="sh-viz sh-viz-checks">
        {["Products", "Cart", "Mobile", "Integrations"].map((label) => (
          <li key={label}><i aria-hidden="true" />{label}</li>
        ))}
      </ul>
    );
  }
  if (index === 6) {
    return (
      <div className="sh-viz sh-viz-launch">
        <div><small>Store</small><strong>Prepared</strong></div>
        <span>Live</span>
        <div className="is-live"><small>Store</small><strong>Published</strong></div>
      </div>
    );
  }
  return (
    <div className="sh-viz sh-viz-flow">
      {["Monitor", "Maintain", "Improve"].map((label, step) => (
        <React.Fragment key={label}>
          {step > 0 ? <i aria-hidden="true" /> : null}
          <span>{label}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

function ProcessTimeline() {
  const [active, setActive] = useState(3);
  const refs = useRef([]);

  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1024px)");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    let observer;

    const bind = () => {
      observer?.disconnect();
      if (desktop.matches || reduced.matches) return;
      const nodes = refs.current.filter(Boolean);
      observer = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(Number(visible.target.dataset.index));
      }, { threshold: [0.6], rootMargin: "-30% 0px -40% 0px" });
      nodes.forEach((node) => observer.observe(node));
    };

    bind();
    desktop.addEventListener("change", bind);
    return () => {
      desktop.removeEventListener("change", bind);
      observer?.disconnect();
    };
  }, []);

  const [title, text] = process[active];

  return (
    <>
      <div className="sh-process" style={{ "--sh-fill": `${(active / (process.length - 1)) * 100}%` }}>
        <div className="sh-process-rail" />
        <div className="sh-process-fill" />
        {process.map(([stepTitle, stepText], index) => (
          <button
            key={stepTitle}
            type="button"
            data-index={index}
            ref={(node) => { refs.current[index] = node; }}
            className={`sh-step ${active === index ? "is-active" : ""}`}
            aria-pressed={active === index}
            onClick={() => setActive(index)}
          >
            <span className="sh-step-mark">{String(index + 1).padStart(2, "0")}</span>
            <span className="sh-step-copy">
              <span>{stepTitle}</span>
              <span>{stepText}</span>
            </span>
          </button>
        ))}
      </div>
      <div className="sh-build-stage" aria-live="polite">
        <div className="sh-build-stage-body" key={active}>
          <p className="sh-build-stage-index">{String(active + 1).padStart(2, "0")}</p>
          <h3>{title}</h3>
          <p className="sh-build-stage-copy">{text}</p>
          <ProcessViz index={active} />
        </div>
      </div>
    </>
  );
}

function FaqList() {
  const [open, setOpen] = useState(null);
  return (
    <div className="border-t border-slate-200">
      {faqs.map(([question, answer], index) => {
        const isOpen = open === index;
        return (
          <div key={question} className={`sh-acc border-b border-slate-200 ${isOpen ? "is-open" : ""}`}>
            <button
              type="button"
              className="w-full flex items-center justify-between gap-6 py-3.5 text-left"
              aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? null : index)}
            >
              <span className="font-semibold text-slate-900 font-heading">{question}</span>
              <span className="sh-plus" aria-hidden="true"><i /><i /></span>
            </button>
            <div className={`sh-acc-panel ${isOpen ? "is-open" : ""}`}>
              <div>
                <p className="pb-5 max-w-3xl text-slate-600 leading-relaxed">{answer}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ShopifyDevelopment() {
  useEffect(() => {
    const previous = document.title;
    document.title = "Shopify Store Development | BizLogic Developers";
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <article data-testid="service-detail-page" className="sh-page bg-white">
      <header className="sh-hero border-b border-slate-200">
        <div className="sh-wrap grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-6">
            <Link to="/services" data-testid="service-detail-back" className="sh-hero-item inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600 mb-5" style={{ animationDelay: "40ms" }}>
              <ArrowLeft className="w-4 h-4" />
              All services
            </Link>
            <p className="sh-hero-item text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 font-heading" style={{ animationDelay: "120ms" }}>
              Shopify store development
            </p>
            <h1 className="sh-hero-item text-4xl sm:text-5xl lg:text-[3.25rem] font-bold tracking-tight text-slate-900 font-heading leading-[1.05]" style={{ animationDelay: "220ms" }}>
              Shopify Stores Built to Sell.
            </h1>
            <p className="sh-hero-item sh-lead max-w-xl text-lg text-slate-600 leading-relaxed" style={{ animationDelay: "360ms" }}>
              Custom Shopify storefronts designed around your products, customers, and the way your business operates.
            </p>
            <div className="sh-hero-item sh-after flex flex-col sm:flex-row gap-3" style={{ animationDelay: "500ms" }}>
              <Link to={CONTACT} data-testid="service-panel-cta-shopify-development" className="group inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3.5 rounded-xl shadow-md shadow-blue-600/10 transition-all hover:-translate-y-0.5">
                Start a Project
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/#portfolio-section" className="group inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-blue-600 text-slate-800 hover:text-blue-600 font-medium px-6 py-3.5 rounded-xl transition-colors">
                View Our Work
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <Storefront />
          </div>
        </div>
      </header>

      <section className="sh-section border-b border-slate-200">
        <div className="sh-wrap">
          <Reveal className="max-w-2xl">
            <Kicker index="01" label="The store experience" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">More Than a Storefront.</h2>
            <p className="sh-lead text-slate-600 leading-relaxed">
              A Shopify store should make it easy for customers to discover products, understand what they&apos;re buying, and complete their purchase.
            </p>
          </Reveal>
          <div className="sh-after">
            <StoreJourney />
          </div>
        </div>
      </section>

      <section className="sh-section border-b border-slate-200 bg-slate-50/70">
        <div className="sh-wrap">
          <Reveal>
            <Kicker index="02" label="What we build" />
            <h2 className="max-w-2xl text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Built Around Your Store.</h2>
          </Reveal>
          <div className="sh-after sh-build-grid">
            {builds.map(([number, glyph, title, text], index) => (
              <Reveal key={title} delay={(index % 2) * 70} className="h-full">
                <article className="sh-card">
                  <span className="sh-card-mark"><ShopGlyph type={glyph} /></span>
                  <span className="sh-card-index">{number}</span>
                  <ArrowRight className="sh-arrow w-4 h-4 text-slate-400" />
                  <h3 className="text-lg font-semibold text-slate-900 font-heading">{title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="sh-section border-b border-slate-200">
        <div className="sh-wrap grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <Reveal>
            <Kicker index="03" label="Product experience" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Make Every Product Easier to Discover.</h2>
            <p className="sh-lead text-slate-600 leading-relaxed">
              The product page is where a shopper decides. The image, the price, the variant, and the next action should be easy to find on any screen.
            </p>
            <ul className="sh-notes">
              {[
                ["Discovery", "Products grouped so people can browse before they commit."],
                ["Clear information", "Title, price, options, and details in one place."],
                ["Mobile", "The same decision, laid out for a phone."],
                ["Trust", "What the product is, stated without extra noise."],
                ["Conversion", "A visible way to add the item and continue."]
              ].map(([label, line]) => (
                <li key={label}>
                  <strong>{label}</strong>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <ProductStage />
        </div>
      </section>

      <section className="sh-section border-b border-slate-200 bg-[#f7f9fc]">
        <div className="sh-wrap">
          <Reveal>
            <Kicker index="04" label="Shopify capabilities" />
            <h2 className="max-w-2xl text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Everything Your Store Needs to Operate.</h2>
          </Reveal>
          <div className="sh-cap-grid sh-after">
            {capabilities.map(([glyph, title, text], index) => (
              <Reveal key={title} delay={(index % 2) * 40} className="h-full">
                <article className="sh-cap">
                  <span className="sh-cap-mark"><ShopGlyph type={glyph} /></span>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="sh-section border-b border-slate-200">
        <div className="sh-wrap grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <Reveal>
            <Kicker index="05" label="Connected commerce" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Your Store Should Work With Your Business.</h2>
            <p className="sh-lead text-slate-600 leading-relaxed">
              A Shopify store can stay connected to the systems a business already uses, when those systems offer an app or a supported connection. The set depends on the operation, not on a fixed list of platforms.
            </p>
          </Reveal>
          <CommerceHub />
        </div>
      </section>

      <section className="sh-section sh-build border-b border-slate-200">
        <div className="sh-wrap">
          <div className="sh-build-intro">
            <Reveal className="max-w-2xl">
              <Kicker index="06" label="How we build" />
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">From Store Idea to Launch.</h2>
              <p className="sh-lead text-slate-600 leading-relaxed">
                Select a step. The active stage is the work in front of you, from the first conversation through support after launch.
              </p>
            </Reveal>
          </div>
          <ProcessTimeline />
        </div>
      </section>

      <section className="sh-section border-b border-slate-200">
        <div className="sh-wrap grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <Reveal>
            <Kicker index="07" label="Mobile shopping" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Designed for Customers on Every Screen.</h2>
            <p className="sh-lead text-slate-600 leading-relaxed">
              Shoppers move between a phone and a larger screen. The product, the price, the variant, and the way into the cart should stay recognizable on both, with the layout changing to fit the device.
            </p>
          </Reveal>
          <DevicePair />
        </div>
      </section>

      <section className="sh-section border-b border-slate-200">
        <div className="sh-wrap">
          <Reveal>
            <Kicker index="08" label="Why BizLogic" />
            <h2 className="max-w-3xl text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">A Store Built Around the Business Behind It.</h2>
          </Reveal>
          <div className="sh-after grid grid-cols-1 md:grid-cols-2 gap-3">
            {principles.map(([title, text], index) => {
              const wide = index === 0 || index === principles.length - 1;
              return (
                <Reveal key={title} delay={(index % 2) * 80} className={wide ? "md:col-span-2" : ""}>
                  <article className={`h-full border border-slate-200 bg-white p-5 sm:p-6 ${wide ? "md:grid md:grid-cols-2 md:gap-8 md:items-center" : ""}`}>
                    <h3 className="text-2xl font-semibold text-slate-900 font-heading">{title}</h3>
                    <p className={`mt-3 text-slate-600 leading-relaxed ${wide ? "md:mt-0" : ""}`}>{text}</p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="sh-section border-b border-slate-200">
        <div className="sh-wrap">
          <Reveal>
            <Kicker index="09" label="FAQ" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Questions about a Shopify store.</h2>
          </Reveal>
          <div className="sh-after">
            <FaqList />
          </div>
        </div>
      </section>

      <section className="sh-cta">
        <div className="sh-cta-grid" />
        <div className="sh-cta-glow" />
        <div className="sh-wrap relative sh-section">
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight font-heading leading-tight">
            Let&apos;s Build a Store Around Your Business.
          </h2>
          <p className="sh-lead text-slate-300 leading-relaxed">
            Tell us what you&apos;re selling, how your store needs to work, and where you want the experience to go.
          </p>
          <div className="sh-after flex flex-col sm:flex-row gap-3">
            <Link to={CONTACT} className="group inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3.5 rounded-xl transition-all hover:-translate-y-0.5">
              Start a Project
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/services" className="group inline-flex items-center justify-center gap-2 border border-slate-600 hover:border-white text-white font-semibold px-6 py-3.5 rounded-xl transition-colors">
              Explore Services
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}
